import axios from 'axios';
import api from '@src/api';
import moment from 'moment';
import findIndex from 'lodash/findIndex';
import { cloneDeep } from 'lodash';

export const state = {
  allPlatelists: [],
  adminPlanWeek: null,
  initAllPlatelists: [],
  isAddingDish: false,
  isSavingDish: false,
  selectedPlatelist: null,
  selectedMealtime: null,
  selectedMealtimeDish: null,
  selectedPlanDay: null,
  selectedWeekOffset: 0,
  clearMealPlanFilters: false,
  showPlatelistArchivedOverlay: true,
  hasUnsavedPlatelistChanges: false,
  discardPlatelistChanges: false,
  platelistFilter: {},
};

export const getters = {};

export const mutations = {
  setAllPlatelists(state, value) {
    state.allPlatelists = value;
  },
  setInitAllPlatelists(state, value) {
    state.initAllPlatelists = value;
  },
  setFilterTrainerOnlyPlatelists(state, value) {
    if (
      state.allPlatelists &&
      state.allPlatelists &&
      state.allPlatelists.length
    ) {
      const allPlatelists = cloneDeep(state.allPlatelists);
      const filteredPlatelists = allPlatelists.filter(
        (platelist) => value !== platelist.editabilityStatus.locked
      );
      state.allPlatelists = filteredPlatelists;
    }
  },
  setClearMealPlanFilters(state, value) {
    state.clearMealPlanFilters = value;
  },
  setCreatePlatelist(state, value) {
    state.allPlatelists.unshift(value);
  },
  setUpdatePlatelistItem(state, value) {
    const i = state.allPlatelists.findIndex((item) => item.id === value.id);
    state.allPlatelists[i] = value;
    state.selectedPlatelist = value;
  },
  setNewPlatelistPlaceholder(state, value) {
    state.allPlatelists.unshift(value);
  },
  setRemovePlatelistPlaceholder(state) {
    state.allPlatelists.shift();
  },
  setSelectedPlatelist(state, value) {
    state.selectedPlatelist = value;
  },
  setSelectedMealtime(state, value) {
    state.selectedMealtime = value;
  },
  setSelectedPlanDay(state, value) {
    state.selectedPlanDay = value;
  },
  setSelectedWeekOffset(state, value) {
    state.selectedWeekOffset = value;
  },
  setAdminPlanWeek(state, value) {
    state.adminPlanWeek = value;
  },
  setIsAddingDish(state, value) {
    state.isAddingDish = value;
  },
  setIsSavingDish(state, value) {
    state.isSavingDish = value;
  },
  setSelectedMealtimeDish(state, value) {
    state.selectedMealtimeDish = value;
  },
  setShowPlatelistArchivedOverlay(state, value) {
    state.showPlatelistArchivedOverlay = value;
  },
  setHasUnsavedPlatelistChanges(state, value) {
    state.hasUnsavedPlatelistChanges = value;
  },
  setDiscardPlatelistChanges(state, value) {
    state.discardPlatelistChanges = value;
  },
  setPlateListFilter(state, value) {
    state.platelistFilter = value;
  },
};

export const actions = {
  init({ state, dispatch, rootState }) {
    if (rootState.auth && rootState.auth.currentUser) {
      dispatch('getAllPlatelists');
    }
  },
  getAllPlatelists(ctx) {
    return axios
      .get(api.URL_GET_ALL_PLATELISTS)
      .then((res) => {
        ctx.commit('setAllPlatelists', res.data);
        ctx.commit('setInitAllPlatelists', res.data);
        return res;
      })
      .catch((err) => {
        console.error(err);
        ctx.commit('setAllPlatelists', []);
        return null;
      });
  },

  getPlatelist(ctx, platelistObj) {
    const url = api.URL_GET_PLATELIST.replace(':platelistId', platelistObj.id);

    return axios
      .get(url)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  },

  getAdminPlanGrid(
    ctx,
    { platelistId, weekOffset = 0, platelistType = 'Challenge' }
  ) {
    const today = todayAtMidnight();
    const requestPayload = {
      platelistId: platelistId,
      today: today,
      offsetFromThisWeek: weekOffset,
      daysBeingUpdated: 7,
    };

    return axios
      .post(api.URL_GET_PLAN_GRID, requestPayload)
      .then((res) => {
        // Sort weekdays if they come back out of order
        res.data.sort((a, b) => {
          return a.dateOffset - b.dateOffset;
        });

        const oneDay = 86400000;
        const lastSunday = getLastSunday();
        const requestedWeekSunday = moment(
          lastSunday + weekOffset * oneDay * 7
        );
        const useDates =
          platelistType === 'Infinite' || platelistType === 'Point In Time';

        let planWeek = buildPlanWeek(
          platelistId,
          requestedWeekSunday,
          weekOffset,
          useDates
        );
        planWeek = buildPlanDays(
          planWeek,
          requestedWeekSunday,
          weekOffset,
          oneDay,
          useDates
        );
        planWeek = buildPlanDayDishes(res.data, planWeek);
        planWeek = organizeLeftovers(planWeek);
        ctx.commit('setAdminPlanWeek', planWeek);
        return planWeek;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  },
  createPlatelist(ctx, platelistObj) {
    return axios
      .post(api.URL_CREATE_PLATELIST, platelistObj.payload)
      .then((res) => {
        if (res.data.error) {
          console.error('Error: ', res.data.error);
        } else {
          ctx.commit('setRemovePlatelistPlaceholder', false);
          ctx.commit('setCreatePlatelist', res.data);
          return res.data;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  updatePlatelist(ctx, platelistObj) {
    const url = api.URL_UPDATE_PLATELIST.replace(
      ':platelistId',
      platelistObj.id
    );

    // Replace stale property 'platelistTypeName', due to api update, if it exists
    const hasPlatelistTypeName = Object.prototype.hasOwnProperty.call(
      platelistObj,
      'platelistTypeName'
    );
    const hasPlatelisttypename = Object.prototype.hasOwnProperty.call(
      platelistObj,
      'platelisttypename'
    );
    const hasLowercaseImageUrl = Object.prototype.hasOwnProperty.call(
      platelistObj,
      'imageurl'
    );

    if (hasPlatelistTypeName || hasPlatelisttypename) {
      hasPlatelistTypeName
        ? delete platelistObj.platelistTypeName
        : delete platelistObj.platelisttypename;
      platelistObj.type = 'Challenge';
    }

    if (hasLowercaseImageUrl) {
      delete platelistObj.imageurl;
    }

    const requestPayload = { ...platelistObj };

    return axios
      .put(url, requestPayload)
      .then((res) => {
        ctx.commit('setUpdatePlatelistItem', res.data);
        return res;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  },

  addMealToPlatelist(
    ctx,
    { platelistDayId, mealTimeId, recipeId, useLeftovers }
  ) {
    const payload = {
      action: 'add',
      platelistDayId: platelistDayId,
      mealId: null,
      mealTimeId: mealTimeId,
      recipeId: recipeId,
      useLeftovers: useLeftovers,
    };

    return axios
      .put(api.URL_MANAGE_PLATELIST_DISH, payload)
      .then((res) => {
        if (res.data.error) {
          console.error('Error: ', res.data.error);
        } else {
          return res.data;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  removeMealFromPlatelist(ctx, platelistMealId) {
    const payload = { action: 'remove', platelistMealId };

    return axios
      .put(api.URL_MANAGE_PLATELIST_DISH, payload)
      .then((res) => {
        if (res.data.error) {
          console.error('Error: ', res.data.error);
        } else {
          return res.data;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },

  updateLeftoverDish(ctx, leftoverObj) {
    const payload = {
      leftoverServings: -1,
      mealId: null,
      ...leftoverObj,
    };

    return axios
      .put(api.URL_MANAGE_PLATELIST_DISH, payload)
      .then((res) => {
        if (res.data.error) {
          console.error('Error: ', res.data.error);
        } else {
          return res.data;
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  },
};

// Private Helpers

function getMonthNames(type = 'long') {
  if (type === 'short') {
    return [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
  } else if (type === 'long') {
    return [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
  }
}

function getDayNames(type = 'long') {
  if (type === 'short') {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  } else if (type === 'long') {
    return [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
  }
}

function todayAtMidnight() {
  return moment().format('YYYY-MM-DD') + `T00:00:00.000Z`;
}

function getLastSunday() {
  return moment().startOf('week');
}

function formattedWeekOf(months, requestedWeekSunday) {
  return `Week of ${
    months[requestedWeekSunday.format('MMM')]
  } ${requestedWeekSunday.format('D')}`;
}

function getHeaderDate(days, months, requestedWeekSunday) {
  return `${days[0]}, ${
    months[requestedWeekSunday.format('MMM')]
  } ${requestedWeekSunday.format('D')}`;
}

function buildPlanWeek(platelistId, requestedWeekSunday, weekOffset, useDates) {
  const daysLong = getDayNames();
  const monthsShort = getMonthNames('short');
  const monthsLong = getMonthNames();

  return {
    platelistId: platelistId,
    weekOf: useDates
      ? formattedWeekOf(monthsShort, requestedWeekSunday)
      : `Week ${weekOffset + 1}`,
    planWeekSnacks: {
      // planWeekSnacks is "Almost entirely" deprecated...
      // We ¿Should? be able to remove this and the sections below
      // checking for "Snacks"... post V1...
      headerDate: useDates
        ? getHeaderDate(daysLong, monthsLong, requestedWeekSunday)
        : `Snack Day`,
      headerMealTime: 'Snacks',
      dishCount: 0,
      dateString: requestedWeekSunday.toISOString(),
      meals: [],
      recipes: [],
    },
    planDays: [],
    allLeftoverOptions: [],
  };
}

function buildPlanDays(
  planGrid,
  requestedWeekSunday,
  weekOffset,
  oneDay,
  useDates
) {
  const daysShort = getDayNames('short');
  const daysLong = getDayNames();
  const monthsLong = getMonthNames();
  const timeSlotDefaultData = (dayIndex, timeString) => {
    return {
      headerDate: useDates
        ? `${daysLong[dayIndex]}, ${
            monthsLong[requestedWeekSunday.format('MMM')]
          } ${moment(requestedWeekSunday + oneDay * dayIndex).format('D')}`
        : `Day ${dayIndex + weekOffset * 7 + 1}`,
      headerMealTime: timeString,
      dishCount: 0,
      meals: [],
      recipes: [],
      leftovers: [],
      dishesAvailableForLeftoverPlaceholder: [],
    };
  };

  for (let i = 0; i < 7; i++) {
    planGrid.planDays[i] = {};
    planGrid.planDays[i].header = useDates
      ? `${daysShort[i]} ${moment(requestedWeekSunday + oneDay * i).format(
          'D'
        )}`
      : `${i + weekOffset * 7 + 1}`;
    planGrid.planDays[i].dateString = `${moment(
      requestedWeekSunday + oneDay * i
    ).toISOString()}`;
    planGrid.planDays[i].Breakfast = {
      ...timeSlotDefaultData(i, 'Breakfast'),
      id: 1,
    };
    planGrid.planDays[i].Lunch = {
      ...timeSlotDefaultData(i, 'Lunch'),
      id: 2,
    };
    planGrid.planDays[i].Dinner = {
      ...timeSlotDefaultData(i, 'Dinner'),
      id: 3,
    };
  }
  return planGrid;
}

function buildPlanDayDishes(res, planWeek) {
  res.forEach((day, i) => {
    if (day.dateOffset % 7 === 0) {
      planWeek.planWeekSnacks.id = day.id;
    }
    planWeek.planDays[day.dateOffset % 7].id = day.id;

    if (day.meals) {
      day.meals.forEach((meal) => {
        if (meal.mealTime.name === 'Snacks') {
          planWeek.planWeekSnacks.meals.push(meal);
          planWeek.planWeekSnacks.dishCount++;
        } else {
          planWeek.planDays[day.dateOffset % 7][meal.mealTime.name].meals.push(
            meal
          );
          planWeek.planDays[day.dateOffset % 7][meal.mealTime.name].dishCount++;
        }
      });
    }

    if (day.recipes) {
      day.recipes.forEach((recipe) => {
        if (recipe.mealTime.name === 'Snacks') {
          planWeek.planWeekSnacks.recipes.push(recipe);
          planWeek.planWeekSnacks.dishCount++;
        } else {
          planWeek.planDays[day.dateOffset % 7][
            recipe.mealTime.name
          ].recipes.push(recipe);
          planWeek.planDays[day.dateOffset % 7][recipe.mealTime.name]
            .dishCount++;
        }
      });
    }

    if (day.leftovers) {
      day.leftovers.forEach((leftover) => {
        planWeek.planDays[day.dateOffset % 7][
          leftover.mealTime.name
        ].leftovers.push(leftover);
        planWeek.planDays[day.dateOffset % 7][leftover.mealTime.name]
          .dishCount++;
      });
    }

    planWeek.planDays[i] = { ...planWeek.planDays[i], ...day };
  });

  return planWeek;
}

function organizeLeftovers(planWeek) {
  // organize the leftovers into proper groups;
  const daySlotLeftoverOptions = [];

  const organizeDayTimeSlot = (day, timeSlot) => {
    const recipeCollection = day[timeSlot].recipes;
    const preparedOnDate = day[timeSlot].headerDate;
    const preparedOnTime = timeSlot;

    recipeCollection.forEach((recipe) => {
      if (recipe.hasLeftovers) {
        // is this recipe in the allLeftoverOptions collection?
        if (
          !planWeek.allLeftoverOptions.some(
            (leftover) => leftover.id === recipe.id
          )
        ) {
          planWeek.allLeftoverOptions.push({
            preparedOnDate,
            preparedOnTime,
            ...recipe,
          });
        }

        const recipeIndex = findIndex(
          daySlotLeftoverOptions,
          (leftover) => leftover.id === recipe.id
        );
        if (recipeIndex >= 0) {
          // there are times where a recipe will be marked as a leftover more than once in a plan
          // if/when this is encountered remove the daySlotLeftoverOptions so that the next push
          // changes it to reference the most recently prepared version of the recipe.
          daySlotLeftoverOptions.splice(recipeIndex, 1);
        }
        daySlotLeftoverOptions.push(recipe);
      }
    });
  };

  planWeek.planDays.forEach((day) => {
    day.Breakfast.dishesAvailableForLeftoverPlaceholder = cloneDeep(
      daySlotLeftoverOptions
    );
    organizeDayTimeSlot(day, 'Breakfast');

    day.Lunch.dishesAvailableForLeftoverPlaceholder = cloneDeep(
      daySlotLeftoverOptions
    );
    organizeDayTimeSlot(day, 'Lunch');

    day.Dinner.dishesAvailableForLeftoverPlaceholder = cloneDeep(
      daySlotLeftoverOptions
    );
    organizeDayTimeSlot(day, 'Dinner');
  });

  return planWeek;
}
